/**
 * NOM-035 Guide III - Full Page Questionnaire
 * Página completa para aplicar el cuestionario de 72 preguntas de factores de riesgo psicosocial
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { 
  ArrowLeft, 
  ArrowRight, 
  CheckCircle, 
  ClipboardList,
  Clock,
  AlertTriangle,
  Save,
  Send
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import logger from '../utils/logger';

const NOM035Guide3Page = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { language } = useLanguage();
  
  const [loading, setLoading] = useState(true);
  const [started, setStarted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [questionnaire, setQuestionnaire] = useState(null);
  const [currentDomainIndex, setCurrentDomainIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  
  const empresaId = user?.empresa_id;
  const userId = user?.id;
  
  // Cargar las preguntas al montar
  useEffect(() => {
    if (empresaId) {
      loadQuestions();
    }
  }, [empresaId, language]);
  
  const loadQuestions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/v5/nom035/${empresaId}/guide3/questions?lang=${language}`);
      if (response.data.success) {
        setQuestionnaire(response.data.data);
      }
    } catch (error) {
      logger.error('Error loading guide3 questions:', error);
      toast.error(language === 'es' ? 'Error al cargar el cuestionario' : 'Error loading questionnaire');
    } finally {
      setLoading(false);
    }
  };
  
  const handleAnswer = (itemId, value) => {
    setAnswers(prev => ({ ...prev, [itemId]: value }));
  };
  
  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`/v5/nom035/${empresaId}/guide3/submit?user_id=${userId}`, {
        respuestas: answers
      });
      
      setCompleted(true);
      toast.success(language === 'es' ? 'Cuestionario completado exitosamente' : 'Questionnaire completed successfully');
      
      setTimeout(() => {
        navigate('/dashboard');
      }, 3000);
    } catch (error) {
      logger.error('Error submitting guide3:', error);
      toast.error(language === 'es' ? 'Error al guardar las respuestas' : 'Error saving responses');
    } finally {
      setSubmitting(false);
    }
  };
  
  const currentDomain = questionnaire?.dominios?.[currentDomainIndex];
  const totalDomains = questionnaire?.dominios?.length || 0;
  const totalItems = questionnaire?.total_items || 0;
  const answeredCount = Object.keys(answers).length;
  const progress = totalItems > 0 ? Math.round((answeredCount / totalItems) * 100) : 0;
  
  // Verificar si todas las preguntas del dominio actual están respondidas
  const currentDomainComplete = currentDomain?.items?.every(item => answers[item.id] !== undefined) || false;
  const allAnswered = answeredCount === totalItems;
  
  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-gray-500">{language === 'es' ? 'Cargando cuestionario...' : 'Loading questionnaire...'}</p>
        </div>
      </div>
    );
  }
  
  // Completed state
  if (completed) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="pt-8 text-center">
            <CheckCircle className="w-20 h-20 mx-auto text-green-500 mb-6" />
            <h2 className="text-2xl font-bold text-green-700 mb-2">
              {language === 'es' ? '¡Cuestionario Completado!' : 'Questionnaire Completed!'}
            </h2>
            <p className="text-gray-600 mb-6">
              {language === 'es' 
                ? 'Gracias por completar la evaluación. Tus respuestas han sido guardadas.'
                : 'Thank you for completing the assessment. Your responses have been saved.'}
            </p>
            <p className="text-sm text-gray-500">
              {language === 'es' 
                ? 'Redirigiendo al dashboard...'
                : 'Redirecting to dashboard...'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Intro state
  if (!started) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-2xl mx-auto">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/dashboard')}
            className="mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {language === 'es' ? 'Volver al dashboard' : 'Back to dashboard'}
          </Button>
          
          <Card>
            <CardHeader className="text-center pb-2">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ClipboardList className="w-8 h-8 text-teal-600" />
              </div>
              <CardTitle className="text-2xl">
                {language === 'es' 
                  ? 'Guía III - Identificación de Factores de Riesgo Psicosocial'
                  : 'Guide III - Identification of Psychosocial Risk Factors'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <p className="text-center text-gray-600">
                {language === 'es'
                  ? 'Este cuestionario evalúa los factores de riesgo psicosocial en tu entorno de trabajo según la NOM-035-STPS-2018.'
                  : 'This questionnaire evaluates psychosocial risk factors in your work environment according to NOM-035-STPS-2018.'}
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg text-center">
                  <ClipboardList className="w-8 h-8 text-teal-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-gray-800">{totalItems}</p>
                  <p className="text-sm text-gray-500">{language === 'es' ? 'Preguntas' : 'Questions'}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg text-center">
                  <Clock className="w-8 h-8 text-teal-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-gray-800">20-30</p>
                  <p className="text-sm text-gray-500">{language === 'es' ? 'Minutos aprox.' : 'Minutes approx.'}</p>
                </div>
              </div>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-yellow-800 mb-1">
                      {language === 'es' ? 'Instrucciones importantes' : 'Important instructions'}
                    </p>
                    <ul className="text-sm text-yellow-700 space-y-1">
                      <li>• {language === 'es' ? 'Responde con honestidad basándote en tu experiencia laboral' : 'Answer honestly based on your work experience'}</li>
                      <li>• {language === 'es' ? 'No hay respuestas correctas o incorrectas' : 'There are no right or wrong answers'}</li>
                      <li>• {language === 'es' ? 'Tus respuestas son confidenciales' : 'Your responses are confidential'}</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className="text-center pt-4">
                <Button 
                  onClick={() => setStarted(true)} 
                  size="lg"
                  className="bg-teal-600 hover:bg-teal-700 px-8"
                >
                  {language === 'es' ? 'Comenzar Cuestionario' : 'Start Questionnaire'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }
  
  // Questionnaire state
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header fijo */}
      <div className="sticky top-0 z-10 bg-white border-b shadow-sm">
        <div className="max-w-5xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate('/dashboard')}
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                {language === 'es' ? 'Salir' : 'Exit'}
              </Button>
              <h1 className="text-lg font-semibold text-gray-800">
                {language === 'es' ? 'Guía III - Factores de Riesgo' : 'Guide III - Risk Factors'}
              </h1>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-500">
                {answeredCount}/{totalItems} {language === 'es' ? 'respondidas' : 'answered'}
              </span>
              <Badge variant={progress === 100 ? 'default' : 'secondary'} className={progress === 100 ? 'bg-green-600' : ''}>
                {progress}%
              </Badge>
            </div>
          </div>
          
          {/* Progress bar */}
          <Progress value={progress} className="h-2" />
          
          {/* Domain tabs */}
          <div className="flex gap-2 mt-3 overflow-x-auto pb-2">
            {questionnaire?.dominios?.map((domain, idx) => {
              const domainComplete = domain.items.every(item => answers[item.id] !== undefined);
              const isActive = idx === currentDomainIndex;
              return (
                <button
                  key={domain.id}
                  onClick={() => setCurrentDomainIndex(idx)}
                  className={`px-3 py-1.5 text-xs rounded-full whitespace-nowrap transition-all flex items-center gap-1 ${
                    isActive 
                      ? 'bg-teal-600 text-white shadow-md' 
                      : domainComplete 
                        ? 'bg-green-100 text-green-700 border border-green-300'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {domainComplete && !isActive && <CheckCircle className="w-3 h-3" />}
                  <span>{idx + 1}. {domain.nombre?.length > 25 ? domain.nombre.substring(0, 25) + '...' : domain.nombre}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
      
      {/* Contenido principal */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        {currentDomain && (
          <div className="space-y-4">
            {/* Título del dominio */}
            <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 mb-6">
              <h2 className="text-lg font-semibold text-teal-800">
                {language === 'es' ? 'Sección' : 'Section'} {currentDomainIndex + 1}: {currentDomain.nombre}
              </h2>
              <p className="text-sm text-teal-600 mt-1">
                {currentDomain.items.length} {language === 'es' ? 'preguntas en esta sección' : 'questions in this section'}
              </p>
            </div>
            
            {/* Preguntas */}
            <div className="space-y-4">
              {currentDomain.items.map((item, idx) => {
                const isAnswered = answers[item.id] !== undefined;
                return (
                  <Card key={item.id} className={`transition-all ${isAnswered ? 'border-green-200 bg-green-50/30' : ''}`}>
                    <CardContent className="pt-4">
                      <div className="flex items-start gap-3 mb-4">
                        <span className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                          isAnswered ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-600'
                        }`}>
                          {idx + 1}
                        </span>
                        <p className="text-gray-800 pt-1">{item.texto}</p>
                      </div>
                      
                      <div className="flex flex-wrap gap-2 ml-11">
                        {questionnaire.escala.map(opt => (
                          <button
                            key={opt.value}
                            onClick={() => handleAnswer(item.id, opt.value)}
                            className={`px-4 py-2 text-sm rounded-lg border transition-all ${
                              answers[item.id] === opt.value
                                ? 'bg-teal-600 text-white border-teal-600 shadow-md'
                                : 'bg-white text-gray-600 border-gray-300 hover:border-teal-400 hover:bg-teal-50'
                            }`}
                          >
                            {language === 'es' ? opt.label_es : opt.label_en}
                          </button>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}
      </div>
      
      {/* Footer fijo con navegación */}
      <div className="sticky bottom-0 bg-white border-t shadow-lg">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <Button
              variant="outline"
              onClick={() => setCurrentDomainIndex(prev => Math.max(0, prev - 1))}
              disabled={currentDomainIndex === 0}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {language === 'es' ? 'Sección anterior' : 'Previous section'}
            </Button>
            
            <span className="text-sm text-gray-500">
              {language === 'es' ? 'Sección' : 'Section'} {currentDomainIndex + 1} {language === 'es' ? 'de' : 'of'} {totalDomains}
            </span>
            
            {currentDomainIndex < totalDomains - 1 ? (
              <Button
                onClick={() => setCurrentDomainIndex(prev => prev + 1)}
                disabled={!currentDomainComplete}
                className="bg-teal-600 hover:bg-teal-700"
              >
                {language === 'es' ? 'Siguiente sección' : 'Next section'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={!allAnswered || submitting}
                className="bg-green-600 hover:bg-green-700"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {language === 'es' ? 'Enviando...' : 'Sending...'}
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    {language === 'es' ? 'Finalizar cuestionario' : 'Finish questionnaire'}
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NOM035Guide3Page;
